import { Overlay } from "@dropbox/dig-components/overlay";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import * as React from "react";
import { ButtonAnchor } from "../../button-anchor";
import { CalendarIcon } from "../../calendar-icon/CalendarIcon";
import { Metadata } from "../../metadata";
import { Attendee } from "../attendee";
import {
  _CalendarCardContent as CardContent,
  _CalendarContainer as Container,
} from "../container";
import type { CalendarEvent, CalendarEventLabels } from "../types";
import styles from "./CalendarMultiEventCard.module.css";
import { DeclinedEventsPopover } from "./DeclinedEventsPopover";

export interface CalendarMultiEventCardProps {
  events: CalendarEvent[];
  declinedEvents: CalendarEvent[];
  labels: Pick<
    CalendarEventLabels,
    | "getNumAttendeesLabel"
    | "getFormattedEventTime"
    | "dateFormatterFn"
    | "getFullDate"
    | "getDeclinedEventLabel"
  >;
}

interface EventItemProps {
  event: CalendarEvent;
  labels: Pick<
    CalendarEventLabels,
    "getNumAttendeesLabel" | "getFormattedEventTime"
  >;
}

const EventItem = ({ event, labels }: EventItemProps) => {
  const formattedTime = React.useMemo(() => {
    return labels.getFormattedEventTime({
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.allDay,
    });
  }, [labels, event.startTime, event.endTime, event.allDay]);

  return (
    <div className={styles.eventItem}>
      <div className={styles.eventHeader}>
        <Text size="small" className={styles.eventTitle}>
          <Truncate tooltipControlProps={{ auto: true, placement: "top" }}>
            {event.title}
          </Truncate>
        </Text>

        <Metadata withDividers="bullet">
          <Metadata.Item>
            <Metadata.Label>
              <Text size="medium" color="subtle">
                {formattedTime}
              </Text>
            </Metadata.Label>
          </Metadata.Item>
          {event.attendees && event.attendees.length > 0 && (
            <Metadata.Item>
              <Attendee
                attendees={event.attendees}
                labels={{
                  getNumAttendeesLabel: labels.getNumAttendeesLabel,
                }}
                isSubtleLabel={true}
              />
            </Metadata.Item>
          )}
        </Metadata>
      </div>
    </div>
  );
};

export const CalendarMultiEventCard = ({
  events,
  declinedEvents,
  labels,
}: CalendarMultiEventCardProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);

  // Use the first event's date for the calendar display
  const eventDate = React.useMemo(
    () =>
      events && events.length > 0 ? new Date(events[0].startTime) : new Date(),
    [events],
  );

  const fullDate = React.useMemo(() => {
    return labels.getFullDate(eventDate);
  }, [eventDate, labels]);

  const declinedEventLabel = React.useMemo(() => {
    if (declinedEvents.length === 0) {
      return null;
    }
    return labels.getDeclinedEventLabel(declinedEvents.length);
  }, [labels, declinedEvents]);

  const handleTriggerClick = React.useCallback(() => {
    setIsPopoverOpen(!isPopoverOpen);
  }, [isPopoverOpen]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsPopoverOpen(!isPopoverOpen);
      } else if (event.key === "Escape" && isPopoverOpen) {
        setIsPopoverOpen(false);
        triggerRef.current?.focus();
      }
    },
    [isPopoverOpen],
  );

  const handleMouseEnter = React.useCallback(() => {
    setIsPopoverOpen(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const handleFocus = React.useCallback(() => {
    setIsPopoverOpen(true);
  }, []);

  const handleBlur = React.useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const handleOverlayClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const leftColumn = (
    <>
      <CalendarIcon
        date={eventDate}
        dateFormatterFn={labels.dateFormatterFn}
        size="xxlarge"
      />
      <div>
        <Title
          weightVariant="emphasized"
          size="medium"
          tagName="h3"
          className={styles.dateTitle}
        >
          {fullDate}
        </Title>

        <Box display="flex" justifyContent="center" marginTop="Micro XSmall">
          <Text size="large" color="subtle">
            {events.length} meetings
          </Text>
        </Box>
      </div>
    </>
  );

  const rightColumn = (
    <div className={styles.eventsContainer}>
      {events.map((event, index) => (
        <React.Fragment key={event.id}>
          <EventItem
            event={event}
            labels={{
              getNumAttendeesLabel: labels.getNumAttendeesLabel,
              getFormattedEventTime: labels.getFormattedEventTime,
            }}
          />
          {index < events.length - 1 && <div className={styles.eventDivider} />}
        </React.Fragment>
      ))}

      {declinedEventLabel && (
        <React.Fragment>
          {events.length > 0 && (
            <Box
              marginTop="Micro XSmall"
              marginLeft="Micro Large"
              marginRight="Micro Large"
              style={{ height: "1px" }}
            />
          )}
          <Box
            position="relative"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
          >
            <Box
              display="flex"
              paddingTop="Micro Small"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              ref={triggerRef}
            >
              <ButtonAnchor
                className={styles.declinedEventButton}
                onClick={handleTriggerClick}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                aria-expanded={isPopoverOpen}
                aria-haspopup="dialog"
                aria-describedby={
                  isPopoverOpen ? "declined-events-popover" : undefined
                }
                resetStyles={true}
              >
                <Text size="large" color="standard">
                  {declinedEventLabel}
                </Text>
              </ButtonAnchor>
            </Box>
            {isPopoverOpen && (
              <Overlay
                placement="top"
                offsetDistance={0}
                anchorRef={triggerRef}
                auto={true}
                className={styles.popoverContainer}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={handleOverlayClick}
              >
                <div
                  id="declined-events-popover"
                  role="dialog"
                  aria-label={declinedEventLabel}
                >
                  <DeclinedEventsPopover
                    declinedEvents={declinedEvents}
                    labels={{
                      getNumAttendeesLabel: labels.getNumAttendeesLabel,
                      getFormattedEventTime: labels.getFormattedEventTime,
                    }}
                  />
                </div>
              </Overlay>
            )}
          </Box>
        </React.Fragment>
      )}
    </div>
  );

  return (
    <Container>
      <CardContent
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        leftColumnAlignment="center"
      />
    </Container>
  );
};
